import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { cn } from '@/utils/cn';
import { ROLE_DESCRIPTIONS } from '../data/role-descriptions';
import { Deck, Count, Tagline } from './role-stack-parts';

const FACE =
  'relative flex h-full flex-col items-center justify-center gap-xs rounded-xl border border-border-subtle bg-surface-elevated p-xs text-center transition-colors duration-200 group-hover:border-brand-red/40 sm:p-md';

type Props = {
  role: string;
  count: number | undefined;
  to: string | null;
  failed: boolean;
};

type FaceProps = Omit<Props, 'to'> & { locked: boolean };

function Face({ role, count, failed, locked }: FaceProps): ReactNode {
  const info = ROLE_DESCRIPTIONS[role];
  return (
    <div className={cn(FACE, locked && 'opacity-60')}>
      {locked && (
        <Lock
          size={14}
          className="absolute right-sm top-sm text-text-tertiary"
        />
      )}
      <span className="text-[9px] font-semibold uppercase tracking-wider text-text-secondary sm:text-xs">
        {info?.title ?? role}
      </span>
      <Count count={count} failed={failed} />
      <Tagline tagline={info?.tagline} locked={locked} />
    </div>
  );
}

// Rol bloqueado (`to: null`): no navega ni recibe foco, y se queda sin el
// abanico de hover para que se lea que ahí no hay nada que abrir.
export function RoleStackCard({ role, count, to, failed }: Props): ReactNode {
  const body = (
    <div className="relative aspect-[5/7]">
      <Deck />
      <Face role={role} count={count} failed={failed} locked={to === null} />
    </div>
  );
  if (!to)
    return (
      <div className="cursor-not-allowed" aria-disabled="true">
        {body}
      </div>
    );
  return (
    <Link
      to={to}
      className="group rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red/50"
    >
      {body}
    </Link>
  );
}
